/**
 * The app's dropdown, on the right-click menu's shape.
 *
 * A native `<select>` draws whatever the operating system draws: a different
 * height on every platform, no icons, no second line, and nothing in common
 * with the menu that opens when you right-click a column three inches away.
 * This is that menu, opened from a button instead of a pointer — same card,
 * same border, same 28px rows, same shadow.
 *
 * It positions itself `fixed` from the trigger's rectangle rather than
 * absolutely inside it, because these open inside popovers and scrolling
 * panels, and an absolutely-placed dropdown in an `overflow: auto` parent is a
 * dropdown with its bottom half cut off.
 */
import {
  Fragment, useEffect, useLayoutEffect, useRef, useState,
  type KeyboardEvent as ReactKeyboardEvent, type ReactNode,
} from 'react'
import type { FieldIcon } from '@rowted/shared'
import {
  IconCheck, COLUMN_ICONS, IconChevronDown, IconChevronUp, IconClose, IconPlus, IconSearch,
} from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import { placeFloating } from '../lib/place-floating.js'
import { controlClass } from './form/field.js'
import { usePhone } from '../lib/use-narrow.js'
import { PanelSheet } from './panel-sheet.js'

export interface MenuOption {
  value: string
  label: string
  /**
   * A second line under the label — Board 8's menus are a name and one line of
   * what it means (`On a date` / `Pick the day, or leave it blank and schedule
   * later`). Drawn with the row's existing two-line shape.
   */
  help?: string
  /**
   * A glyph that is not a column's — a kind of visit, a way a visit starts
   * (Board 8). Drawn where `icon` is, on the row and on the trigger.
   */
  glyph?: ReactNode
  /**
   * The tile wears the thing's own tint rather than the menu's grey — a visit
   * picked from a list is the 40px visit tile at 28 (`accent-soft`, the glyph
   * in `accent`), so the row reads as the card it will become (Aaron, step 23
   * item 1: the grey squares "look like icon placeholders").
   */
  tileTone?: 'accent'
  /**
   * The trigger shows the label alone — Board 8's Starts draws ↳ on its
   * trigger for *After another visit* and nothing for *On a date*, while every
   * row of the open menu carries its tile.
   */
  plainTrigger?: boolean
  /**
   * What the closed trigger reads when it is not the row's label — the When
   * block's units, listed plural (`business days`) and read singular beside a
   * 1 (`1 business day`, Board 8 state 4).
   */
  triggerLabel?: string
  /** The same glyph the column header wears, when the option is a column. */
  icon?: FieldIcon
  /** Right-aligned, muted — a count, a unit, a sort direction. */
  detail?: string
  /**
   * Muted, immediately after the label — what the label is short for.
   *
   * A state row is "PA" then "Pennsylvania": the code is what is stored and
   * what a column shows, and the name is what makes the code readable by
   * somebody who does not know it. Not `detail`, which is right-aligned and
   * therefore reads as a fact *about* the row rather than as the rest of its
   * name.
   */
  sub?: string
  /**
   * Extra text the search box matches, beyond the label.
   *
   * Typing "penn" has to find PA. Without this the filter sees only the
   * label, so a menu whose labels are codes is a menu you can only search if
   * you already know the code — which is the thing a picker exists to save
   * you from.
   */
  search?: string
  /**
   * A colour dot in place of the icon, for a menu whose options *are* colours.
   *
   * A CSS colour, because the only caller resolves a palette key into one
   * first — a swatch is the one place where the thing being chosen is the
   * colour itself, and naming seven greens in words is not a picker.
   */
  swatch?: string
  /**
   * A heading above this option, drawn when it differs from the one before.
   *
   * What `<optgroup>` was for: the seventeen date windows are unreadable as one
   * list and obvious as "Days · Weeks · Months · Quarters · Years".
   */
  group?: string
}

/** Label or `search`, case-insensitively. */
const matchesQuery = (option: MenuOption, query: string): boolean => {
  const wanted = query.trim().toLowerCase()
  if (wanted.length === 0) return true
  return option.label.toLowerCase().includes(wanted)
    || (option.search ?? '').toLowerCase().includes(wanted)
}

/** Rows above this many get a search box; below it, searching is in the way. */
const SEARCH_FROM = 9
const ROW = 28

interface Anchor { left: number; top: number; width: number; maxHeight: number }

/**
 * The shell: a trigger that opens a floating menu, and closes it on Escape, on
 * a click outside, or on a scroll that would leave it stranded.
 */

function useMenu() {
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState<Anchor | null>(null)

  useLayoutEffect(() => {
    if (!open) { setAnchor(null); return }
    const rect = trigger.current?.getBoundingClientRect()
    if (!rect) return
    /*
     * `placeFloating` — the one rule every floating thing follows.
     *
     * This used to do its own arithmetic and finish with
     * `Math.max(FLOOR, room)`, which *inflates* the cap: a trigger with 80px
     * under it was handed a 220px panel, and 140px of menu sat below the fold
     * on a page that cannot scroll to reach it. That is the Type dropdown on
     * the property flyout running off the bottom of the screen.
     */
    const at = placeFloating({
      anchor: rect,
      width: Math.max(rect.width, 200),
      wanted: Math.min(panel.current?.scrollHeight ?? 0, 360),
    })
    setAnchor({ left: at.left, top: at.top, width: rect.width, maxHeight: at.maxHeight })

    /*
     * The caret goes in the search box, once there is one and once it can
     * take focus.
     *
     * `autoFocus` on the input did not work and neither did focusing it on
     * mount: the panel is measured before it is placed, so until `anchor` is
     * set it wears `invisible` — and `focus()` on a `visibility: hidden`
     * element is a no-op in a browser. jsdom does not model that, which is
     * why this looked fine in a component test and was broken on the screen:
     * opening the 56-row State menu left focus on the button, and typing
     * "penn" went nowhere. A frame later the panel is visible and the focus
     * takes.
     *
     * Every menu with nine or more rows has this box, so this is the one
     * place it gets the caret.
     */
    const timer = window.requestAnimationFrame(() => {
      panel.current?.querySelector('input')?.focus()
    })
    return () => window.cancelAnimationFrame(timer)
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const dismiss = (event: MouseEvent) => {
      if (panel.current?.contains(event.target as Node)) return
      if (trigger.current?.contains(event.target as Node)) return
      setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.stopPropagation(); setOpen(false) }
    }
    const timer = window.setTimeout(() => window.addEventListener('mousedown', dismiss), 0)
    window.addEventListener('keydown', escape, true)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('mousedown', dismiss)
      window.removeEventListener('keydown', escape, true)
    }
  }, [open])

  return { open, setOpen, trigger, panel, anchor }
}

function MenuPanel({
  anchor, panelRef, minWidth, children, className,
}: {
  anchor: Anchor | null
  panelRef: ReturnType<typeof useMenu>['panel']
  minWidth: number
  children: ReactNode
  /** The chips mode's panel: no inset, the popup radius (qb-05). */
  className?: string
}) {
  return (
    <div
      ref={panelRef}
      role="menu"
      className={cn(
        'fixed z-[80] overflow-y-auto border border-line bg-surface shadow-popover',
        className ?? 'rounded-card py-1',
        // Measured before it is placed, so it must not flash at 0,0.
        anchor ? 'visible' : 'invisible',
      )}
      style={{
        left: anchor?.left ?? 0,
        top: anchor?.top ?? 0,
        minWidth: Math.max(minWidth, anchor?.width ?? 0),
        maxHeight: Math.min(anchor?.maxHeight ?? 360, 360),
      }}
    >
      {children}
    </div>
  )
}

/**
 * "＋ Create a new billing plan" — the menu's way out.
 *
 * The same row `ComboInput` draws, in the same words and the same accent, so
 * the two controls make the same offer. It is the last row and it is divided
 * off, because it is not one of the answers: it is what to do when none of
 * them is the answer.
 */
function CreateRow({
  query, thing, divided, onCreate,
}: { query: string; thing?: string; divided: boolean; onCreate: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onCreate}
      className={cn('flex w-full items-center gap-2 px-2.5 py-[9px] text-left',
        'text-[13px] font-semibold text-accent hover:text-accent-dark',
        divided && 'mt-1 border-t border-line-faint')}
    >
      <IconPlus size={14} />
      {query
        ? `Create “${query}” as a new ${thing ?? 'one'}`
        : `Create a new ${thing ?? 'one'}`}
    </button>
  )
}

function MenuSearch({
  value, onChange, placeholder, onKeyDown,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
  /** ↑↓ and ⏎, which belong to the list rather than to the box. */
  onKeyDown?: (event: ReactKeyboardEvent<HTMLInputElement>) => void
}) {
  return (
    /* The ring is the box's, not the inner input's — see `AddSearch`. */
    <label className={cn('relative mx-1 mb-1 flex h-8 items-center rounded-field bg-field px-2',
      'focus-within:ring-2 focus-within:ring-accent-ring')}>
      <span className="sr-only">{placeholder}</span>
      <span className="pointer-events-none text-text-muted"><IconSearch size={14} /></span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={cn(
          'h-full w-full bg-transparent pl-2 text-cell text-text',
          'placeholder:text-text-muted focus:outline-none focus-visible:shadow-none',
        )}
      />
    </label>
  )
}

function OptionRow({
  option, selected, checkbox, active, onSelect, height, tickLeft, subLine, divided, ruled,
}: {
  option: MenuOption
  selected: boolean
  checkbox?: boolean
  /** The ruled list (cb-12 · Terms): see `MenuSelectProps.ruled`. */
  ruled?: boolean
  /** The lookup look's taller rows, its left tick, a second line and a rule above (cb-04 · 4). */
  height?: number
  tickLeft?: boolean
  subLine?: string
  divided?: boolean
  /**
   * Where ↑↓ has got to, which is not the same as what is chosen.
   *
   * A menu of 56 states opens on the one you picked last time and moves from
   * wherever the arrows have taken you; drawing both as one state would mean
   * the caret vanishes the moment you arrow off your own answer.
   */
  active?: boolean
  onSelect: () => void
}) {
  const Icon = option.icon ? COLUMN_ICONS[option.icon] : null
  const line = subLine ?? option.help

  /*
   * Board 8's list: a 28px tile, the name at 13.5/600 and one line of help,
   * a tick at the right — "a plain list, one line of help". Every menu whose
   * rows carry `help` is drawn this way, so the kind, the starts, the repeats,
   * the ends, the unit and the event menus cannot drift apart.
   */
  /*
   * cb-12's Terms list: one indent for every row — a 16px column holding the
   * ✓ of the chosen one — then the name at 13, 600 only when chosen, and its
   * help under it at 11 in the faint ink. 7 × 14 around each row, a hairline
   * between them, the chosen row tinted. No tile, whatever the row carries.
   */
  if (ruled) {
    return (
      <button
        type="button"
        role="menuitemradio"
        data-active={active ? '' : undefined}
        data-ruled-row=""
        aria-checked={selected}
        onClick={onSelect}
        className={cn(
          'flex w-full items-center gap-2.5 px-3.5 py-[7px] text-left text-[13px] text-text',
          'border-t border-line-faint first:border-t-0',
          selected ? 'bg-accent-soft/40' : 'hover:bg-line-soft',
          active && !selected && 'bg-line-soft',
        )}
      >
        <span aria-hidden className="flex w-4 shrink-0 justify-center text-accent">
          {selected && <IconCheck size={14} />}
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className={cn('truncate', selected ? 'font-semibold' : 'font-normal')}>{option.label}</span>
          {line && <span className="text-[11px] text-text-faint">{line}</span>}
        </span>
      </button>
    )
  }

  if (option.help !== undefined && !subLine && !checkbox && !tickLeft) {
    return (
      <button
        type="button"
        role="menuitemradio"
        data-active={active ? '' : undefined}
        data-help-row=""
        aria-checked={selected}
        onClick={onSelect}
        className={cn(
          'flex w-full items-center gap-[11px] px-3 py-[9px] text-left text-text',
          'border-t border-line-faint first-of-type:border-t-0',
          selected ? 'bg-accent-soft/40' : 'hover:bg-line-soft',
          active && !selected && 'bg-line-soft',
        )}
      >
        <span
          aria-hidden
          className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px]',
            option.tileTone === 'accent'
              ? 'border border-accent-line bg-accent-soft text-accent'
              : selected ? 'bg-accent-soft text-accent' : 'bg-line-soft text-text-muted')}
        >
          {option.glyph}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-px">
          <b className="truncate text-[13.5px] font-semibold">{option.label}</b>
          <span className="text-[11.5px] leading-[1.3] text-text-muted">{option.help}</span>
        </span>
        {/* A visit's price, a plan's count — the same right-hand fact the one-line rows carry. */}
        {option.detail && (
          <span className="tnum shrink-0 whitespace-nowrap text-[12.5px] font-semibold text-text">
            {option.detail}
          </span>
        )}
        {selected && <span aria-hidden className="flex shrink-0 text-accent"><IconCheck size={15} /></span>}
      </button>
    )
  }

  return (
    <button
      type="button"
      role={checkbox ? 'menuitemcheckbox' : 'menuitemradio'}
      data-active={active ? '' : undefined}
      aria-checked={selected}
      onClick={onSelect}
      style={{ height: line ? undefined : height ?? ROW }}
      className={cn(
        'flex w-full items-center gap-2 px-2.5 text-left text-cell',
        tickLeft
          ? cn('px-3.5 text-[13.5px]', selected ? 'bg-accent-soft/50 font-semibold text-text'
            : 'text-text hover:bg-line-soft')
          : selected && !checkbox ? 'bg-accent-soft font-semibold text-accent-dark'
            : 'text-text hover:bg-line-soft',
        active && !selected && 'bg-line-soft',
        line && 'py-2',
        divided && 'border-t border-line-faint',
      )}
    >
      {tickLeft && (
        <span aria-hidden className="flex w-4 shrink-0 justify-center text-accent">
          {selected && <IconCheck size={14} />}
        </span>
      )}
      {/*
        * Drawn, not a <Checkbox>: the row is already a button, and a real
        * input inside it would take the click twice and nest a label in a
        * button. The tick is the same mark the Checkbox primitive draws.
        */}
      {checkbox && (
        <span
          aria-hidden
          className={cn(
            'flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border',
            selected ? 'border-accent bg-accent text-surface' : 'border-line-strong bg-surface',
          )}
        >
          {selected && (
            <svg viewBox="0 0 16 16" className="h-3 w-3" aria-hidden>
              <path d="M3.5 8.5l3 3 6-6" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
      )}
      {option.swatch && (
        <span
          aria-hidden
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ background: option.swatch }}
        />
      )}
      {Icon && <span className="shrink-0 text-text-muted"><Icon size={14} /></span>}
      {!Icon && option.glyph && (
        <span aria-hidden className="flex shrink-0 text-text-muted">{option.glyph}</span>
      )}
      {line
        ? (
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate">{option.label}</span>
            <span className="truncate text-[11.5px] font-normal text-text-faint">{line}</span>
          </span>
        )
        : (
          <span className={cn('min-w-0 truncate', option.sub ? 'shrink-0' : 'flex-1',
            tickLeft && !selected && 'font-medium')}>
            {option.label}
          </span>
        )}
      {option.sub && (
        <span className="min-w-0 flex-1 truncate text-text-muted">{option.sub}</span>
      )}
      {option.detail && (
        <span className="shrink-0 text-sub text-text-muted">{option.detail}</span>
      )}
    </button>
  )
}

/**
 * `size` rather than a height through `className`: `cn` joins, it does not
 * merge, so `h-8 h-6` leaves both on the element and the stylesheet picks —
 * which is how the Summarize rows came out four pixels taller than every other
 * menu's.
 */
export type MenuSize = 'default' | 'small' | 'field'

/*
 * The radius and the font live here rather than in the shared string below.
 *
 * They used to sit in the base, so a caller handing in `controlClass()` got
 * *both* — `cn` is a join, not a merge, and whichever rule Tailwind happened
 * to emit last won. That is how the package builder ended up with dropdowns
 * at radius 10 and 12.5px beside text boxes at radius 9 and 13.5px, all
 * nominally 36 tall and none of them matching.
 *
 * `field` is the record form's control: the same box `controlClass()` draws,
 * so a menu beside a text input is the same object.
 */
const TRIGGER_SIZES: Record<Exclude<MenuSize, 'field'>, string> = {
  default: 'h-8 px-2 rounded-field text-cell',
  small: 'h-6 px-1.5 rounded-field text-cell',
}

/**
 * The trigger's shape.
 *
 * `field` is the kit's control, which fills the space it is given like every
 * other control does — a trigger that sized to its content looked right as a
 * pill and collapsed to a sliver in a form. `auto` asks for the pill, and it
 * has to be asked for *inside* `controlClass` rather than appended after it:
 * `cn` is a join, so `w-full w-auto` leaves both rules live and CSS source
 * order decides. It decided `w-full`, which is how the "When:" dropdown on a
 * package visit came to be 380px wide and push its own sentence onto three
 * lines after `auto` had supposedly been passed.
 */
const triggerSize = (size: MenuSize, auto: boolean): string =>
  (size === 'field'
    ? controlClass({
      width: auto ? 'w-auto shrink-0' : 'w-full',
      className: 'justify-start gap-1.5',
    })
    : TRIGGER_SIZES[size])

export interface MenuSelectProps {
  value: string | null
  onChange: (value: string | null) => void
  options: MenuOption[]
  /** What the trigger reads when nothing is chosen. */
  placeholder?: string
  /** Screen-reader name for the trigger. */
  label: string
  /**
   * Size to the content rather than filling the row — for a trigger that is a
   * tag in a line rather than a field in a form.
   */
  auto?: boolean
  /**
   * The trigger's own id, so a `Field`'s `<label for>` has something to point
   * at. Without it the label names nothing: clicking the word does not open
   * the menu, and a screen reader reads the label and stops.
   */
  id?: string
  /** The help or error line under a `Field`. */
  describedBy?: string
  /** Adds a "—" row that clears the choice. */
  clearable?: boolean
  clearLabel?: string
  searchPlaceholder?: string
  className?: string
  minWidth?: number
  disabled?: boolean
  size?: MenuSize
  /**
   * Draw the trigger's insides instead of the swatch-label-chevron default,
   * and drop its border with them.
   *
   * For a trigger that is a glyph and nothing else: the note toolbar's colour
   * button is Word's A-over-a-bar, 24px square, sitting in a row of plain
   * toolbar buttons with no room for a word or a chevron.
   */
  triggerContent?: (chosen: MenuOption | null) => ReactNode
  /**
   * Faint words after the chosen label in the closed field only — `Bed area`
   * then `· sq ft` (cb-04 · Measured by). The rows keep `detail` at the right.
   */
  triggerSub?: (chosen: MenuOption) => string
  /**
   * Do not take focus when the trigger is pressed.
   *
   * A menu that acts on the text you have selected — the note editor's — must
   * not be the thing that unselects it, and a button takes focus on mousedown,
   * which collapses the selection before the handler ever runs.
   */
  keepSelection?: boolean
  /**
   * Make one from here, rather than going somewhere else to make it first.
   *
   * The last row of the menu, divided off — the same offer `ComboInput` has
   * carried since the field kit, and for the same reason: every dropdown in
   * this product names something that lives in another table, and the old
   * answer was to send somebody to a different screen and lose what they were
   * doing. Aaron: "it would also be nice if in the edit visit popup in the
   * billing plan drop down, it had the add new at the bottom like we've been
   * standardizing lookup drop downs around the app."
   *
   * It is given the typed query when there is one, so a menu somebody has
   * searched in can name what they were looking for.
   */
  onCreate?: (query: string) => void
  /** The noun in "Create a new …". */
  createThing?: string
  /*
   * ── The lookup look (builder-engine §6.7, board cb-04 · 4) ──
   *
   * Additive: a caller that passes none of these is the menu it always was.
   */
  /** Taller rows — the measurement-type lookup's 38. */
  rowHeight?: number
  /** A tick at the left of the chosen row, room for it on the others. */
  tickLeft?: boolean
  /** The clear row last rather than first, with a second line under it. */
  clearLast?: boolean
  clearSub?: string
  /** A quiet note under the rows, divided from them. */
  footNote?: ReactNode
  /**
   * On a phone the menu is a **list sheet** instead of a drop-down (visits
   * §4.6, Board 17 cell 3): the field's name as the title, one line under it,
   * a row per answer — name, its help, a radio at the right — and ‹ back to
   * the form. Without it a phone gets the drop-down, as before.
   */
  sheet?: { title: string; sub?: string }
  /**
   * No search box, however many rows. For a short, fixed list the board draws
   * without one — the nine rows of Terms (§7.4) — where a box to type into is
   * a control nobody needs above rows they can read at a glance.
   */
  noSearch?: boolean
  /**
   * The ruled list (board cb-12, the Terms menu): every row at one indent
   * behind a ✓ column, two-line rows at their ordinary weight, a hairline
   * between rows, no tile, in a 12px-radius panel with no inset. Opt-in;
   * left out, rows are drawn as before.
   */
  ruled?: boolean
}

/** One choice out of many, with the chosen one's icon on the trigger. */
export function MenuSelect({
  value, onChange, options, placeholder = 'Choose…', label, id, describedBy, auto, clearable,
  clearLabel = '—', searchPlaceholder = 'Find…', className, minWidth = 200, disabled,
  size = 'default', triggerContent, triggerSub, keepSelection, onCreate, createThing,
  rowHeight, tickLeft, clearLast, clearSub, footNote, sheet, noSearch, ruled,
}: MenuSelectProps) {
  const { open, setOpen, trigger, panel, anchor } = useMenu()
  const phone = usePhone()
  const asSheet = Boolean(sheet && phone)
  const [query, setQuery] = useState('')
  useEffect(() => { if (!open) setQuery('') }, [open])

  const chosen = options.find((option) => option.value === value) ?? null
  const Icon = chosen?.icon ? COLUMN_ICONS[chosen.icon] : null
  const shown = query.trim()
    ? options.filter((option) => matchesQuery(option, query))
    : options

  /*
   * ↑↓ and ⏎, on a menu that had neither.
   *
   * A 56-row list you can filter but not commit from the keyboard is a list
   * that makes you type "penn" and then reach for the mouse. The active row
   * starts at the top of whatever is showing, so typing enough to leave one
   * match and pressing ⏎ is the whole interaction.
   */
  const [active, setActive] = useState(0)
  useEffect(() => { setActive(0) }, [query, open])

  const onSearchKey = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (shown.length === 0) return
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((at) => (at + step + shown.length) % shown.length)
      return
    }
    if (event.key !== 'Enter') return
    event.preventDefault()
    const picked = shown[active]
    // ⏎ on a search that matches nothing is not a choice; it does nothing.
    if (!picked) return
    onChange(picked.value)
    setOpen(false)
  }

  return (
    <>
      <button
        ref={trigger}
        type="button"
        id={id}
        disabled={disabled}
        aria-label={label}
        aria-describedby={describedBy}
        aria-haspopup="menu"
        aria-expanded={open}
        onMouseDown={keepSelection ? (event) => event.preventDefault() : undefined}
        onClick={() => setOpen(!open)}
        className={cn(
          'flex items-center text-text disabled:opacity-50',
          triggerContent
            ? cn('justify-center text-text-muted hover:bg-line-soft hover:text-text',
              open && 'bg-line-soft text-text')
            : cn(triggerSize(size, Boolean(auto)),
              size === 'field'
                ? 'hover:border-line-strong'
                : cn('gap-1.5 border border-line bg-surface hover:border-line-strong',
                  auto && 'shrink-0'),
              open && 'border-accent-line ring-2 ring-accent-ring'),
          className,
        )}
      >
        {triggerContent ? triggerContent(chosen) : (
          <>
            {chosen?.swatch && (
              <span
                aria-hidden
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: chosen.swatch }}
              />
            )}
            {Icon && <span className="shrink-0 text-text-muted"><Icon size={14} /></span>}
            {!Icon && chosen?.glyph && !chosen.plainTrigger && (
              <span aria-hidden className="flex shrink-0 text-text-muted">{chosen.glyph}</span>
            )}
            <span className={cn('truncate text-left',
              chosen?.sub || (chosen && triggerSub) ? 'shrink-0' : 'min-w-0 flex-1',
              !chosen && 'min-w-0 flex-1 text-text-muted')}>
              {chosen ? (chosen.triggerLabel ?? chosen.label) : placeholder}
            </span>
            {chosen && triggerSub
              ? (
                <span className="min-w-0 flex-1 truncate text-left text-[12px] text-text-faint">
                  {triggerSub(chosen)}
                </span>
              )
              : chosen?.sub && (
                <span className="min-w-0 flex-1 truncate text-left text-text-muted">
                  {chosen.sub}
                </span>
              )}
            <span className="shrink-0 text-text-muted"><IconChevronDown size={14} /></span>
          </>
        )}
      </button>

      {asSheet && sheet && (
        <PanelSheet
          open={open}
          onClose={() => setOpen(false)}
          onBack={() => setOpen(false)}
          backLabel=""
          title={sheet.title}
          sub={sheet.sub}
          layer={70}
        >
          <div className="flex flex-col" role="menu">
            {options.map((option) => {
              const on = option.value === value
              return (
                <button
                  key={option.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={on}
                  data-sheet-row=""
                  onClick={() => { onChange(option.value); setOpen(false) }}
                  className={cn('flex min-h-[56px] w-full items-center gap-3 border-b border-line-faint px-4 py-2.5 text-left',
                    on ? 'bg-accent-soft/40' : 'bg-surface')}
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <b className="text-[15px] font-semibold text-text">{option.label}</b>
                    {option.help && <span className="text-[12.5px] leading-[1.35] text-text-muted">{option.help}</span>}
                  </span>
                  <span aria-hidden className={cn('flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px]',
                    on ? 'border-accent' : 'border-line-strong')}>
                    {on && <span className="h-2.5 w-2.5 rounded-full bg-accent" />}
                  </span>
                </button>
              )
            })}
          </div>
        </PanelSheet>
      )}

      {open && !asSheet && (
        <MenuPanel anchor={anchor} panelRef={panel} minWidth={minWidth}
          className={ruled ? 'rounded-[12px]' : undefined}>
          {!noSearch && options.length >= SEARCH_FROM && (
            <MenuSearch
              value={query}
              onChange={setQuery}
              placeholder={searchPlaceholder}
              onKeyDown={onSearchKey}
            />
          )}
          {clearable && !clearLast && (
            <OptionRow
              option={{ value: '', label: clearLabel }}
              selected={value === null}
              onSelect={() => { onChange(null); setOpen(false) }}
            />
          )}
          {shown.map((option, index) => (
            <Fragment key={option.value}>
              {option.group && option.group !== shown[index - 1]?.group && (
                <p className="px-2.5 pb-0.5 pt-1.5 text-label uppercase text-text-faint">
                  {option.group}
                </p>
              )}
              <OptionRow
                option={option}
                selected={option.value === value}
                active={index === active}
                height={rowHeight}
                tickLeft={tickLeft}
                ruled={ruled}
                onSelect={() => { onChange(option.value); setOpen(false) }}
              />
            </Fragment>
          ))}
          {clearable && clearLast && (
            <OptionRow
              option={{ value: '', label: clearLabel }}
              subLine={clearSub}
              selected={value === null}
              height={rowHeight}
              tickLeft={tickLeft}
              divided
              onSelect={() => { onChange(null); setOpen(false) }}
            />
          )}
          {footNote && (
            <p className="m-0 border-t border-line-faint px-3.5 py-2.5 text-[11.5px] leading-[1.4] text-text-faint">
              {footNote}
            </p>
          )}
          {shown.length === 0 && !onCreate && (
            <p className="px-2.5 py-1.5 text-cell text-text-muted">Nothing matches.</p>
          )}
          {onCreate && (
            <CreateRow
              query={query}
              thing={createThing}
              divided={shown.length > 0}
              onCreate={() => { setOpen(false); onCreate(query.trim()) }}
            />
          )}
        </MenuPanel>
      )}
    </>
  )
}

/**
 * The related drop-down, opened from a **button** rather than a field.
 *
 * `MenuSelect` is a field: it holds a value and its trigger says what it is.
 * Some menus answer "add which one?" instead — **+ Add visits ▾** on a choice
 * box (step 22 item 2.4) — and those were drawn as a context menu, a
 * different look from every lookup in the product. This is the same panel,
 * the same rows, the same search box past nine rows and the same divided
 * `＋ Create a new …` row last, hung off whatever button the caller draws.
 */
export function MenuButton({
  options, onPick, onCreate, createThing, searchPlaceholder = 'Find…', minWidth = 280,
  className, children, disabled,
}: {
  options: MenuOption[]
  onPick: (value: string) => void
  /** The divided last row — given the typed query when there is one. */
  onCreate?: (query: string) => void
  /** The noun in "Create a new …". */
  createThing?: string
  searchPlaceholder?: string
  minWidth?: number
  /** The button's own look; the caller draws it like any other button on its bar. */
  className?: string
  children: ReactNode
  disabled?: boolean
}) {
  const { open, setOpen, trigger, panel, anchor } = useMenu()
  const [query, setQuery] = useState('')
  useEffect(() => { if (!open) setQuery('') }, [open])
  const shown = query.trim()
    ? options.filter((option) => matchesQuery(option, query))
    : options

  return (
    <>
      <button
        ref={trigger}
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className={className}
      >
        {children}
      </button>
      {open && (
        <MenuPanel anchor={anchor} panelRef={panel} minWidth={minWidth}>
          {options.length >= SEARCH_FROM && (
            <MenuSearch value={query} onChange={setQuery} placeholder={searchPlaceholder} />
          )}
          {shown.map((option) => (
            <OptionRow
              key={option.value}
              option={option}
              selected={false}
              onSelect={() => { setOpen(false); onPick(option.value) }}
            />
          ))}
          {shown.length === 0 && !onCreate && (
            <p className="px-2.5 py-1.5 text-cell text-text-muted">Nothing matches.</p>
          )}
          {onCreate && (
            <CreateRow
              query={query.trim()}
              thing={createThing}
              divided={shown.length > 0}
              onCreate={() => { setOpen(false); onCreate(query.trim()) }}
            />
          )}
        </MenuPanel>
      )}
    </>
  )
}

export interface MenuMultiSelectProps {
  values: string[]
  onChange: (values: string[]) => void
  options: MenuOption[]
  label: string
  placeholder?: string
  searchPlaceholder?: string
  className?: string
  minWidth?: number
  /**
   * Search on the server instead of in the list.
   *
   * A relation's options are a page of rows out of thousands, so filtering
   * the page is filtering the wrong thing: typing a property that is not on
   * it would say "Nothing matches" about a property that exists. When this is
   * given, the caller fetches and `options` is whatever came back.
   */
  onQuery?: (query: string) => void
  /** Show the search box however few rows are on the page (see `onQuery`). */
  searchAlways?: boolean

  /*
   * ── The chips mode (quote-build-refresh §8.2, board qb-05) ──
   *
   * Everything below is additive: a caller that passes none of it is the
   * filters' summary dropdown, unchanged.
   */
  /** Draw the chosen as chips inside the field, each with ✕, instead of "3 selected". */
  chips?: boolean
  /** Section headings, by `MenuOption.group`; drawn only when there are two or more. */
  groups?: { id: string; label: string; onAdd?: () => void; addLabel?: string }[]
  /** Find-or-create: the typed name, and the heading it was typed under. */
  onCreate?: (query: string, groupId?: string) => void
  /** The create row's words for a query — `Create “Silver”`. */
  createLabel?: (query: string, groupId?: string) => ReactNode
  /** Right-aligned on the create row — "first choice". */
  createTag?: string
  /** The last row: "New choice" / "New question". */
  footLabel?: string
  onFoot?: () => void
  /** Shown under the create row when the list has nothing in it yet. */
  emptyText?: ReactNode
  /** Asked before an option of another group is ticked; false refuses (the "Move it?" panel). */
  confirmToggle?: (value: string, groupId: string) => Promise<boolean> | boolean
  /** Drawn above the foot — where the editor puts the Move panel. */
  prompt?: ReactNode
  disabled?: boolean
  /** On a phone the field opens this sheet rather than a drop-down (§10.4, qb-10 · 6). */
  sheet?: { title: string; sub?: string; doneLabel: string; alreadyLabel: string }
  /**
   * The open list sits in the field's own flow — 8px under it, the field's
   * width — rather than floating over the page, so what follows the field
   * (an editor's footer) moves down instead of being covered (qb-05 · 2–3).
   * Desktop only; a phone still gets `sheet`. Left out, the list floats as
   * before.
   */
  inline?: boolean
  /** Told when the list opens and closes — the field's caller hides its read-back while it is open (qb-05 · 2–3). */
  onOpenChange?: (open: boolean) => void
}

/**
 * Several choices out of many, as a dropdown of checkboxes.
 *
 * The advanced filter used to lay every option out in an open box, so "status
 * is any of" on a list with eleven statuses pushed the rest of the condition
 * off the bottom of the dialogue. A dropdown costs one click and gives the
 * row back its height.
 */
export function MenuMultiSelect(props: MenuMultiSelectProps) {
  if (props.chips) return <ChipsMultiSelect {...props} />
  return <SummaryMultiSelect {...props} />
}

function SummaryMultiSelect({
  values, onChange, options, label, placeholder = 'Choose…',
  searchPlaceholder = 'Find…', className, minWidth = 200, onQuery, searchAlways,
}: MenuMultiSelectProps) {
  const { open, setOpen, trigger, panel, anchor } = useMenu()
  const [query, setQuery] = useState('')
  useEffect(() => { if (!open) { setQuery(''); onQuery?.('') } }, [open])

  const shown = query.trim() && !onQuery
    ? options.filter((option) => matchesQuery(option, query))
    : options

  const chosen = options.filter((option) => values.includes(option.value))
  const summary = chosen.length === 0 ? placeholder
    : chosen.length <= 2 ? chosen.map((option) => option.label).join(', ')
      : `${chosen.length} selected`

  const toggle = (value: string) => {
    onChange(values.includes(value)
      ? values.filter((entry) => entry !== value)
      : [...values, value])
  }

  return (
    <>
      <button
        ref={trigger}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={cn(
          'flex h-8 w-full items-center gap-1.5 rounded-field border border-line bg-surface px-2',
          'text-cell text-text hover:border-line-strong',
          open && 'border-accent-line ring-2 ring-accent-ring',
          className,
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate text-left',
          chosen.length === 0 && 'text-text-muted')}>
          {summary}
        </span>
        {chosen.length > 0 && (
          <span className="tnum shrink-0 rounded-full bg-accent-soft px-1.5 text-[11px] font-semibold text-accent">
            {chosen.length}
          </span>
        )}
        <span className="shrink-0 text-text-muted"><IconChevronDown size={14} /></span>
      </button>

      {open && (
        <MenuPanel anchor={anchor} panelRef={panel} minWidth={minWidth}>
          {(searchAlways || options.length >= SEARCH_FROM) && (
            <MenuSearch
              value={query}
              onChange={(next) => { setQuery(next); onQuery?.(next) }}
              placeholder={searchPlaceholder}
            />
          )}
          {shown.map((option) => (
            <OptionRow
              key={option.value}
              option={option}
              checkbox
              selected={values.includes(option.value)}
              onSelect={() => toggle(option.value)}
            />
          ))}
          {shown.length === 0 && (
            <p className="px-2.5 py-1.5 text-cell text-text-muted">Nothing matches.</p>
          )}
        </MenuPanel>
      )}
    </>
  )
}

/*
 * ── The chips mode ─────────────────────────────────────────────────────────
 *
 * Board qb-05: a field holding the chosen as purple chips, a panel with a
 * find-or-create box, 40px checkbox rows with a count on the right, headings
 * only when there are two groups, and a foot row. On a phone the same field
 * opens qb-10 · 6's sheet — no drop-down on a phone.
 */

/** The choice tone `VisitTag tone="choice"` already uses — chips and headings. */
const CHOICE_TONE = {
  background: 'rgb(var(--status-quoteLine-deployment-bg))',
  color: 'rgb(var(--status-quoteLine-deployment-fg))',
}

function Tick({ on, size = 17 }: { on: boolean; size?: number }) {
  return (
    <span
      aria-hidden
      style={{ width: size, height: size }}
      className={cn('flex shrink-0 items-center justify-center rounded-[5px] border-[1.5px]',
        on ? 'border-accent bg-accent text-surface' : 'border-line-strong bg-surface')}
    >
      {on && (
        <svg viewBox="0 0 16 16" className="h-3 w-3" aria-hidden>
          <path d="M3.5 8.5l3 3 6-6" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  )
}

function ChipsMultiSelect({
  values, onChange, options, label, placeholder = 'Choose…', searchPlaceholder = 'Find…',
  groups = [], onCreate, createLabel, createTag, footLabel, onFoot, emptyText,
  confirmToggle, prompt, disabled, sheet, inline, onOpenChange,
}: MenuMultiSelectProps) {
  const phone = usePhone()
  const { open, setOpen, trigger, panel, anchor } = useMenu()
  useEffect(() => { onOpenChange?.(open) }, [open])
  /*
   * In the flow, the list can open below the editor's fold; bring it into
   * the scroller's view once it is drawn. `scrollIntoView` is absent in jsdom.
   */
  useEffect(() => {
    if (!open || !inline || phone) return undefined
    const timer = window.requestAnimationFrame(() => {
      panel.current?.scrollIntoView?.({ block: 'nearest' })
    })
    return () => window.cancelAnimationFrame(timer)
  }, [open, inline, phone])
  const [query, setQuery] = useState('')
  /** The heading a "+ Add" pointed the box at, when there are two. */
  const [target, setTarget] = useState<string | undefined>(undefined)
  const [active, setActive] = useState(0)
  const search = useRef<HTMLInputElement>(null)
  useEffect(() => { if (!open) { setQuery(''); setTarget(undefined); setActive(0) } }, [open])

  const headed = groups.length >= 2
  const chosen = options.filter((option) => values.includes(option.value))
  const shown = query.trim()
    ? options.filter((option) => matchesQuery(option, query)
      && (!target || option.group === target))
    : options
  const exact = options.some((option) => option.label.trim().toLowerCase()
    === query.trim().toLowerCase() && (!target || option.group === target))
  const canCreate = Boolean(onCreate && query.trim() && !exact)
  const groupOf = (value: string) => options.find((option) => option.value === value)?.group

  const toggle = async (value: string) => {
    if (values.includes(value)) { onChange(values.filter((entry) => entry !== value)); return }
    const group = groupOf(value)
    const others = values.filter((entry) => groupOf(entry) !== group)
    if (group && others.length > 0) {
      const ok = confirmToggle ? await confirmToggle(value, group) : true
      if (!ok) return
      onChange([...values.filter((entry) => groupOf(entry) === group), value])
      return
    }
    onChange([...values, value])
  }

  const create = () => {
    if (!canCreate) return
    onCreate!(query.trim(), target)
    setQuery('')
  }

  const onKey = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive((at) => Math.min(at + 1, shown.length - 1)) }
    if (event.key === 'ArrowUp') { event.preventDefault(); setActive((at) => Math.max(at - 1, 0)) }
    if (event.key === 'Enter') {
      event.preventDefault()
      if (canCreate) create()
      else if (shown[active]) void toggle(shown[active].value)
    }
  }

  /* The field: chips that wrap, the placeholder when none, a chevron. */
  const field = (
    <div
      ref={trigger as unknown as React.RefObject<HTMLDivElement>}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-haspopup={phone && sheet ? 'dialog' : 'menu'}
      aria-expanded={open}
      aria-disabled={disabled || undefined}
      data-chips-field=""
      onClick={() => { if (!disabled) setOpen(!open) }}
      onKeyDown={(event) => {
        if (disabled) return
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setOpen(!open) }
      }}
      className={cn(
        'flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-field border py-1.5 pl-3 pr-2.5',
        disabled ? 'cursor-default border-line bg-line-faint' : 'cursor-pointer border-line bg-surface',
        open && !disabled && 'border-accent-line ring-2 ring-accent-ring',
      )}
    >
      {chosen.length === 0 && (
        <span className="text-[13.5px] text-text-faint">{placeholder}</span>
      )}
      {chosen.map((option) => (
        <span key={option.value} style={CHOICE_TONE} data-choice-chip=""
          className="inline-flex h-[26px] items-center gap-[5px] whitespace-nowrap rounded-[8px] pl-2.5 pr-[7px] text-[12.5px] font-semibold">
          {option.label}
          {!disabled && (
            <button
              type="button"
              aria-label={`Remove ${option.label}`}
              onClick={(event) => { event.stopPropagation(); void toggle(option.value) }}
              className="flex opacity-70 hover:opacity-100"
            >
              <IconClose size={12} />
            </button>
          )}
        </span>
      ))}
      <span className="ml-auto shrink-0 text-text-faint">
        {open && !phone ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
      </span>
    </div>
  )

  const createRow = canCreate && (
    <button
      type="button"
      role="menuitem"
      onClick={create}
      data-create-row=""
      className={cn('flex w-full items-center gap-2.5 bg-accent-soft text-left font-medium text-text',
        phone ? 'h-14 px-4 text-[15px]' : 'h-10 px-3 text-[13.5px]')}
    >
      <span aria-hidden className={cn('flex shrink-0 items-center justify-center rounded-[6px] bg-accent text-surface',
        phone ? 'h-[22px] w-[22px]' : 'h-5 w-5')}>
        <IconPlus size={phone ? 14 : 12} />
      </span>
      <span className="min-w-0 flex-1 truncate">
        {createLabel ? createLabel(query.trim(), target) : `Create “${query.trim()}”`}
      </span>
      {createTag && <span className="shrink-0 text-[12px] text-text-muted">{createTag}</span>}
    </button>
  )

  const row = (option: MenuOption, index: number, big = false) => (
    <button
      key={option.value}
      type="button"
      role="menuitemcheckbox"
      aria-checked={values.includes(option.value)}
      onClick={() => void toggle(option.value)}
      onMouseEnter={() => setActive(index)}
      className={cn('flex w-full items-center text-left text-text',
        big ? 'h-[52px] gap-3 px-4 text-[15px]' : 'h-10 gap-2.5 px-3 text-[13.5px]',
        !big && index === active && 'bg-accent-soft/40',
        !big && 'hover:bg-accent-soft/40')}
    >
      <Tick on={values.includes(option.value)} size={big ? 22 : 17} />
      <b className="min-w-0 flex-1 truncate font-semibold">{option.label}</b>
      {option.detail && (
        <span className="tnum shrink-0 whitespace-nowrap text-[12px] text-text-faint">{option.detail}</span>
      )}
    </button>
  )

  const list = (big = false) => {
    if (!headed) return shown.map((option, index) => row(option, index, big))
    return groups.map((group) => {
      const inGroup = shown.filter((option) => option.group === group.id)
      if (inGroup.length === 0 && query.trim()) return null
      return (
        <Fragment key={group.id}>
          <div className="flex h-[30px] items-center border-t border-line-faint bg-surface-alt px-3">
            <span className="text-label uppercase text-text-faint">{group.label}</span>
            {group.onAdd !== undefined || onCreate ? (
              <button
                type="button"
                onClick={() => {
                  setTarget(group.id)
                  group.onAdd?.()
                  search.current?.focus()
                }}
                className="ml-auto text-[11.5px] font-semibold text-accent"
              >
                {group.addLabel ?? '+ Add'}
              </button>
            ) : null}
          </div>
          {inGroup.map((option) => row(option, shown.indexOf(option), big))}
        </Fragment>
      )
    })
  }

  const foot = footLabel && (
    <button
      type="button"
      onClick={() => { if (onFoot) onFoot(); else search.current?.focus() }}
      className="flex h-[42px] w-full items-center gap-2.5 border-t border-line-faint px-3 text-left text-[13px] font-semibold text-accent"
    >
      <IconPlus size={14} /> {footLabel}
    </button>
  )

  /* ── Phone: qb-10 · 6 ── */
  if (phone && sheet) {
    const unticked = shown.filter((option) => !values.includes(option.value))
    const ticked = options.filter((option) => values.includes(option.value))
    return (
      <>
        {field}
        <PanelSheet
          open={open}
          onClose={() => setOpen(false)}
          onBack={() => setOpen(false)}
          backLabel=""
          title={sheet.title}
          sub={sheet.sub}
          hideClose
          layer={70}
          action={(
            <button type="button" onClick={() => setOpen(false)}
              className="px-1 text-[16px] font-bold text-accent">
              {sheet.doneLabel}
            </button>
          )}
        >
          <div className="flex flex-col">
            <label className="mx-4 my-3 flex h-[52px] items-center gap-2.5 rounded-[12px] border border-accent-line bg-surface px-4 ring-2 ring-accent-ring">
              <span className="text-text-faint"><IconSearch size={17} /></span>
              <span className="sr-only">{searchPlaceholder}</span>
              <input
                ref={search}
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); create() } }}
                placeholder={searchPlaceholder}
                className="h-full w-full bg-transparent text-[16px] text-text placeholder:text-text-faint focus:outline-none focus-visible:shadow-none"
              />
            </label>
            {createRow}
            {options.length === 0 && emptyText && (
              <p className="m-0 px-4 py-3 text-[13px] leading-[1.45] text-text-muted">{emptyText}</p>
            )}
            {unticked.map((option, index) => row(option, index, true))}
            {ticked.length > 0 && (
              <>
                <span className="mt-3 border-b border-line-faint px-4 pb-2 text-label uppercase text-text-faint">
                  {sheet.alreadyLabel}
                </span>
                {ticked.map((option, index) => (
                  <div key={option.value} className="border-b border-line-faint">
                    {row(option, index, true)}
                  </div>
                ))}
              </>
            )}
            {prompt}
          </div>
        </PanelSheet>
      </>
    )
  }

  const panelBody = (
    <>
      <label className="flex h-[42px] items-center gap-2 border-b border-line-faint px-3">
        <span className="text-text-faint"><IconSearch size={14} /></span>
        <span className="sr-only">{searchPlaceholder}</span>
        <input
          ref={search}
          value={query}
          onChange={(event) => { setQuery(event.target.value); setActive(0) }}
          onKeyDown={onKey}
          placeholder={searchPlaceholder}
          className="h-full w-full bg-transparent text-[13.5px] text-text placeholder:text-text-faint focus:outline-none focus-visible:shadow-none"
        />
      </label>
      {createRow}
      {options.length === 0 && emptyText && (
        <p className="m-0 border-t border-line-faint px-4 py-3 text-[12.5px] leading-[1.45] text-text-muted">
          {emptyText}
        </p>
      )}
      {list()}
      {prompt}
      {!canCreate && foot}
    </>
  )

  /* qb-05 · 2–3: the list in the field's flow, `top: 48px` under a 40px field. */
  if (inline) {
    return (
      <>
        {field}
        {open && (
          <div
            ref={panel}
            role="menu"
            data-chips-list=""
            className="mt-2 w-full overflow-hidden rounded-[12px] border border-line bg-surface shadow-popover"
          >
            {panelBody}
          </div>
        )}
      </>
    )
  }

  return (
    <>
      {field}
      {open && (
        <MenuPanel anchor={anchor} panelRef={panel} minWidth={240} className="rounded-[12px]">
          {panelBody}
        </MenuPanel>
      )}
    </>
  )
}
